import { useEffect } from 'react'

/** Marks an action item so `useActionFocus` can find it; add `ACTION_FOCUS_CLASS` for the highlight. */
export function actionTargetProps(uuid: string) {
  return { 'data-action-uuid': uuid, tabIndex: -1 }
}

export const ACTION_FOCUS_CLASS =
  'scroll-mt-24 outline-none transition-shadow duration-500 data-[focused=true]:ring-2 data-[focused=true]:ring-sky-500/60 data-[focused=true]:ring-offset-2 data-[focused=true]:ring-offset-background'

/**
 * Brings the action item opened from the Action Items page into view once the page has rendered
 * it, moves keyboard focus there and highlights it briefly.
 */
export function useActionFocus(actionUuid: string | undefined, ready: boolean) {
  useFocusTarget(actionUuid ? `[data-action-uuid="${CSS.escape(actionUuid)}"]` : null, ready)
}

/** Marks an email so `useMessageFocus` can find it (an action item card links to its source email). */
export function messageTargetProps(messageId: number) {
  return { 'data-message-id': String(messageId), tabIndex: -1 }
}

/** Brings the email an action item card linked to into view, focused and briefly highlighted (USA-1417). */
export function useMessageFocus(messageId: number | undefined, ready: boolean) {
  useFocusTarget(messageId != null ? `[data-message-id="${messageId}"]` : null, ready)
}

/** Scrolls to the visible element `selector` names, focuses it and highlights it briefly; returns the cleanup. */
export function focusTarget(selector: string) {
  // Some pages render the same item twice (mobile and desktop); pick the visible one.
  const target = Array.from(document.querySelectorAll<HTMLElement>(selector)).find(
    (element) => element.getClientRects().length > 0,
  )
  if (!target) return undefined
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  target.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' })
  target.focus({ preventScroll: true })
  target.dataset.focused = 'true'
  const timer = window.setTimeout(() => delete target.dataset.focused, 2500)
  return () => window.clearTimeout(timer)
}

function useFocusTarget(selector: string | null, ready: boolean) {
  useEffect(() => {
    if (!selector || !ready) return
    return focusTarget(selector)
  }, [selector, ready])
}
